import { RootState } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, DatePicker, Input, Row, Select, Spin, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updatePropsReport } from '../../../../contextRedux/MentenancePlanning/MentenancePlanningModel';
import CompanyService from '../../../../services/company/CompanyService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import UserService from '../../../../services/user/UserService';
import './MentenancePlanningReportTab.css';

const { Option } = Select;

const MentenancePlanningReportTab: React.FC = () => {
  const dispatch = useDispatch();

  const searchTerm = useSelector((state: RootState) => state?.models?.mentenancePlanning?.propsReport?.searchTerm);

  const selectedMonthString = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedMonth
  );

  // Convert string to dayjs object for component use (DatePicker works better with dayjs)
  const selectedMonth = useMemo(() => {
    if (!selectedMonthString) return null;
    try {
      // Parse the ISO string and create a dayjs object
      const dayjsDate = dayjs(selectedMonthString);
      // Ensure it's valid and set to the start of the month
      return dayjsDate.isValid() ? dayjsDate.startOf('month') : null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  }, [selectedMonthString]);

  const selectedTechnician = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedTechnician
  );
  const selectedStatusId = useSelector(
    (state: RootState) => state?.models?.mentenancePlanning?.propsReport?.selectedStatus
  );

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);

  const [pagination, setPagination] = useState<any>({ current: 1, pageSize: 10 });

  const {
    data: mentenancePlannings,
    isFetching: isMentenancePlanningLoading,
    refetch: refetch,
  } = useQuery({
    queryKey: [
      'mentenancePlannings',
      selectedMonthString || moment().format('YYYY-MM'),
      searchTerm,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
    ],
    queryFn: () =>
      MentenancePlanningService.searchAll({
        isAssignedTechnicianIncluded: true,
        isCompanyIncluded: true,
        isContractIncluded: true,
        isMentenancePlanningSystemTypeIncluded: true,
        isStatusIncluded: true,
        isWorkPlaceIncluded: true,
        month: selectedMonth ? selectedMonth.month() + 1 : moment().month() + 1,
        year: selectedMonth ? selectedMonth.year() : moment().year(),
        termen: searchTerm,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician , senior_tehnician',
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (searchTerm === '') {
      refetch();
    }
  }, [searchTerm, refetch]);

  const handleMonthChange = useCallback(
    (date: Dayjs | null) => {
      try {
        // Store as ISO string instead of moment object to avoid serialization issues
        // Use startOf('month') to ensure we only store the month and year, not the specific day/time
        let dateString = null;
        if (date && date.isValid()) {
          // Create a new dayjs object with just year and month to avoid timezone issues
          const year = date.year();
          const month = date.month();
          const cleanDate = dayjs().year(year).month(month).startOf('month');
          // Convert to moment for consistent API calls
          const momentDate = moment(cleanDate.toDate());
          dateString = momentDate.toISOString();
        }

        dispatch(updatePropsReport({ field: 'selectedMonth', value: dateString }));
        dispatch(updatePropsReport({ field: 'selectedStatus', value: null }));
        dispatch(updatePropsReport({ field: 'selectedTechnician', value: null }));
      } catch (error) {
        console.error('Error handling month change:', error);
        // If there's an error, clear the date
        dispatch(updatePropsReport({ field: 'selectedMonth', value: null }));
      }
    },
    [dispatch]
  );

  const handleStatusClick = useCallback(
    (statusId: number | null) => {
      dispatch(updatePropsReport({ field: 'selectedStatus', value: statusId }));
    },
    [dispatch]
  );

  const handleTechnicianChange = useCallback(
    (value: string | null) => {
      dispatch(
        updatePropsReport({
          field: 'selectedTechnician',
          value: value ? _users?.find((u: any) => u.id == value) : null,
        })
      );
    },
    [_users, dispatch]
  );

  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateField({ field: 'skip', value: 0 }));
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize: pageSize,
    });
  };

  const handleSearch = useCallback((value: string) => {
    debouncedSearch(value);
  }, []);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updatePropsReport({ field: 'searchTerm', value }));
    }, 500),
    [dispatch]
  );

  if (isMentenancePlanningLoading || isUsersLoading || isCompaniesLoading || isSystemTypesLoading) {
    return <Spin className='spinner' size='large' />;
  }

  const filteredPlannings = mentenancePlannings;

  const summary = {
    total: filteredPlannings?.length ?? 0,
    assigned: filteredPlannings?.filter((d: any) => d.statusId === 2)?.length ?? 0,
    inProgress: filteredPlannings?.filter((d: any) => d.statusId === 3)?.length ?? 0,
    finalized: filteredPlannings?.filter((d: any) => d.statusId === 4)?.length ?? 0,
    reassigned: filteredPlannings?.filter((d: any) => d.statusId === 5)?.length ?? 0,
    unassigned: filteredPlannings?.filter((d: any) => d.statusId === 1)?.length ?? 0,
  };

  const filteredData = mentenancePlannings?.filter((item: any) => {
    const matchesStatus = selectedStatusId === null || item?.statusId === selectedStatusId;
    const matchesTechnician = selectedTechnician === null || item?.assignedTechnician?.id === selectedTechnician?.id;

    return matchesStatus && matchesTechnician;
  });

  const columns = [
    { title: 'Numar', dataIndex: 'number', key: 'number' },
    { title: 'Inregistrata', dataIndex: ['registeredByUser', 'name'], key: 'registeredByUser' },
    {
      title: 'Companie',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return <div>{company?.name}</div>;
      },
    },
    {
      title: 'Punct de lucru',
      dataIndex: '',
      render: (record: any) => {
        return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
      },
      key: 'workPlace',
    },
    { title: 'Tehnician', dataIndex: ['assignedTechnician', 'name'], key: 'assignedTechnician' },
    {
      title: 'Sisteme',
      dataIndex: 'mentenancePlanningSystemTypes',
      key: 'mentenancePlanningSystemTypes',
      render: (mentenancePlanningSystemTypes: any[]) => (
        <>{mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ')}</>
      ),
    },
    {
      title: 'Numar Contract',
      dataIndex: 'contract',
      render: (contract: any) => {
        return <>{contract?.number}</>;
      },
    },
    { title: 'Status', dataIndex: ['status', 'name'], key: 'status' },
    {
      title: 'Data programata',
      dataIndex: 'appointment',
      key: 'appointment',
      render: (text: string) => (text ? moment(text).format('DD-MM-YYYY') : 'N/A'),
    },
    {
      title: 'Termen limita',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      render: (text: string) => (text ? moment(text).format('DD-MM-YYYY') : 'N/A'),
    },
  ];

  return (
    <div className='table-page-no-h-scroll'>
      <h3 className='maintenance-report-title'>Report Mentenante</h3>
      <Row gutter={16} style={{ marginBottom: '16px', rowGap: '20px' }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Input.Search
            placeholder='Cauta...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <DatePicker
            key={`month-picker-${selectedMonthString || 'default'}`}
            value={selectedMonth}
            picker='month'
            placeholder='Filtreaza dupa luna'
            onChange={handleMonthChange}
            allowClear
            className='month-picker'
            format='MM-YYYY'
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces?.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes?.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Select
            value={selectedTechnician?.id}
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder='Filtreaza dupa Tehnician'
            onChange={handleTechnicianChange}
            optionFilterProp='children'
          >
            {_users?.map((user: any) => (
              <Option key={user.id} value={user.id}>
                {user.name}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row gutter={[16, 16]} justify='center' style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Mentenante</div>
            <Button
              block
              type={selectedStatusId === null ? 'primary' : 'default'}
              onClick={() => handleStatusClick(null)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.total}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Finalizate</div>
            <Button
              block
              type={selectedStatusId === 4 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(4)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.finalized}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Programate</div>
            <Button
              block
              type={selectedStatusId === 2 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(2)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.assigned}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>In Procesare</div>
            <Button
              block
              type={selectedStatusId === 3 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(3)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.inProgress}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Reprogramate</div>
            <Button
              block
              type={selectedStatusId === 5 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(5)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.reassigned}
            </Button>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Neprogramate</div>
            <Button
              block
              type={selectedStatusId === 1 ? 'primary' : 'default'}
              onClick={() => handleStatusClick(1)}
              style={{ fontSize: '18px', fontWeight: 'bold', height: 'auto', padding: '12px' }}
            >
              {summary?.total - summary.finalized - summary.assigned - summary.reassigned - summary?.inProgress}
            </Button>
          </div>
        </Col>
      </Row>

      <div className='table-scroll-wrapper'>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey='number'
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredData?.length ?? 0,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '20', '30', '50'],
            showSizeChanger: true,
          }}
          bordered
          scroll={{ x: 'max-content' }}
        />
      </div>
    </div>
  );
};

export default MentenancePlanningReportTab;
